import { useState, FormEvent } from 'react';
import {
  Sparkles,
  Send,
  Settings,
  Download,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';
import DraftBody from '../../_shared/DraftBody';
import { usePrototype1, REPORT_CHAT_SCOPE } from '../Prototype1Context';

const Screen08FullReportReview = () => {
  const { chatHistoriesBySection, isChatPending, sendChat, goToScreen } = usePrototype1();
  const [input, setInput] = useState('');
  const chatHistory = chatHistoriesBySection[REPORT_CHAT_SCOPE] ?? [];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    await sendChat(message, {
      intent: 'review',
      ui_state: { panel: 'chat-open' },
      response_hint: { shape: 'chat-reply' },
    });
  };

  const handleChip = async (label: string, intent: 'review' | 'qa' | 'draft') => {
    await sendChat(label, {
      intent,
      ui_state: { panel: 'chat-open', last_user_action: `chip: ${label}` },
    });
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => goToScreen('07-dashboard-all-sections-complete')}
              className="text-[#737373] hover:underline"
            >
              Dashboard
            </button>
            <span className="text-[#737373]">/</span>
            <span className="text-[#737373]">Issaquah School District</span>
            <span className="text-[#737373]">/</span>
            <span className="font-medium text-[#0a0a0a]">Full Report Review</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-white rounded-full bg-[#16a34a]">
              Complete
            </span>
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export Report
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex flex-col flex-1 min-w-0 bg-[#fafafa]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5] shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-[#737373] uppercase tracking-widest">
                  EOAA Report 2025 · Issaquah School District
                </span>
                <span className="text-base font-semibold text-[#0a0a0a]">Full Report</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#737373] border border-[#e5e5e5] rounded-md bg-white hover:bg-[#f5f5f5] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-md bg-white hover:bg-[#f5f5f5] transition-colors"
                >
                  Next Section
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-6 px-8 py-6 overflow-y-auto flex-1">
              <h1 className="text-[20px] font-bold text-[#0a0a0a]">Section 1: Introduction</h1>
              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                During the spring of 2025, Issaquah School District (Issaquah) collaborated with the XQ Institute (XQ)
                to examine the high school experience. This was accomplished by undertaking an Educational Outcomes
                Audit &amp; Analysis (EOAA), which brought together educators, students, families, and community members
                to envision a high school experience more relevant and engaging so that all students graduate prepared
                to thrive in a rapidly changing world.
              </p>
              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                XQ is committed to supporting Issaquah as it strives to prepare every student for success in college,
                career, and life.
              </p>
              <h2 className="text-[17px] font-semibold text-[#0a0a0a]">
                Overview of XQ's Educational Outcomes Audit &amp; Analysis (EOAA)
              </h2>
              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                XQ's EOAA examines the journeys that students take through high school, identifying hidden patterns of
                college eligibility and postsecondary readiness.
              </p>

              <div
                className="flex flex-col items-center justify-center w-full rounded bg-[#f5f5f5] border border-[#e5e5e5] gap-2"
                style={{ aspectRatio: '664/447' }}
              >
                <ImageIcon className="w-7 h-7 text-[#a3a3a3]" />
                <span className="text-[13px] text-[#737373]">EOAA introduction · image placeholder</span>
              </div>
              <div className="flex flex-col items-center justify-center w-full h-[280px] rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] gap-2">
                <ImageIcon className="w-7 h-7 text-[#a3a3a3]" />
                <span className="text-[13px] text-[#737373]">EOAA Journey Diagram · image placeholder</span>
              </div>

              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                The analysis uses a range of information to examine how students prepare for college and career on their
                journey through high school. It gathers data from three sources:
              </p>

              <div className="flex flex-col items-center justify-center w-full h-[280px] rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] gap-2">
                <ImageIcon className="w-7 h-7 text-[#a3a3a3]" />
                <span className="text-[13px] text-[#737373]">Three Key Inputs Diagram · image placeholder</span>
              </div>

              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                With this data, XQ generates rigorous and accessible reports for each school that are used by teams to
                capture and prioritize findings, generate ideas to reduce barriers to student success, and map out next
                steps for sharing their ideas with their broader school communities.
              </p>
              <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                The EOAA is designed to spark transformative thinking. It helps schools identify critical areas for
                improvement and create a roadmap to ensure that every student graduates ready for what comes next. It
                begins with listening to students, honoring their lived experiences, and incorporating their ideas into
                school design.
              </p>
            </div>
          </div>

          <div className="flex flex-col w-[420px] shrink-0 border-l border-[#e5e5e5] bg-[#fafafa]">
            <div className="flex items-center justify-between px-4 h-12 border-b border-[#e5e5e5] shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717]">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[13px] font-semibold text-[#0a0a0a]">AI Assistant</span>
              </div>
              <Settings className="w-4 h-4 text-[#737373]" />
            </div>

            <div className="flex flex-col gap-4 flex-1 overflow-y-auto p-4">
              <div className="flex items-start gap-2">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0 mt-0.5">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <div className="rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] px-3 py-2.5 flex-1">
                  <p className="text-[13px] text-[#0a0a0a] leading-[1.5]">
                    The complete report is ready for review. I can help you check for consistency across sections,
                    generate a narrative summary, or highlight key data points.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 pl-9">
                <button
                  type="button"
                  onClick={() => handleChip('Check consistency across sections', 'review')}
                  disabled={isChatPending}
                  className="flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-lg bg-white hover:bg-[#f5f5f5] transition-colors text-left disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#737373] shrink-0" />
                  Check consistency across sections
                </button>
                <button
                  type="button"
                  onClick={() => handleChip('Flag data gaps or missing citations', 'qa')}
                  disabled={isChatPending}
                  className="flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-lg bg-white hover:bg-[#f5f5f5] transition-colors text-left disabled:opacity-50"
                >
                  <AlertTriangle className="w-4 h-4 text-[#737373] shrink-0" />
                  Flag data gaps or missing citations
                </button>
                <button
                  type="button"
                  onClick={() => handleChip('Draft executive summary', 'draft')}
                  disabled={isChatPending}
                  className="flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-lg bg-white hover:bg-[#f5f5f5] transition-colors text-left disabled:opacity-50"
                >
                  <FileText className="w-4 h-4 text-[#737373] shrink-0" />
                  Draft executive summary
                </button>
              </div>

              {chatHistory.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}>
                  {m.role === 'assistant' && (
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0 mt-0.5">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-3 py-2.5 ${
                      m.role === 'user'
                        ? 'bg-[#171717] text-white max-w-[80%]'
                        : 'bg-[#f5f5f5] border border-[#e5e5e5] text-[#0a0a0a] flex-1'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="text-[13px] leading-[1.5]">{m.content}</p>
                    ) : (
                      <DraftBody text={m.content} />
                    )}
                  </div>
                </div>
              ))}
              {isChatPending && <span className="text-xs text-[#737373]">AI is typing…</span>}
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 px-4 py-3 border-t border-[#e5e5e5] shrink-0"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask or instruct the AI..."
                disabled={isChatPending}
                className="flex-1 h-10 px-3 rounded-lg bg-[#fafafa] border border-[#e5e5e5] text-[13px] text-[#0a0a0a] outline-none"
              />
              <button
                type="submit"
                disabled={isChatPending || !input.trim()}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#171717] shrink-0 hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen08FullReportReview;
